import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import { makeGetRequest, makePutRequest, makePostRequest, makeDeleteRequest } from 'utils/api'
import * as Yup from 'yup'
import { Container } from 'reactstrap'
import { useImmer } from 'use-immer'
import { List } from './List'
import {
  fromPipelineDetails,
  toPipelineStages,
  toPipelineStage,
  fromDealsData
} from './serializers'
import update from 'immutability-helper'
import { DealsModal } from './DealsModal'
import { AddButton } from 'components/common/AddButton'
import { PipelineGuideModal } from './PipelineGuideModal'
import s from './Pipeline.scss'
import cloneDeep from 'lodash/cloneDeep'
import { AddPipeline } from './AddPipeline'

const editValidationSchema = Yup.object().shape({
  name: Yup.string().required('Stage Name field is required!'),
  label: Yup.string().required('Stage Label field is required!'),
  category: Yup.lazy(val =>
    typeof val === 'object'
      ? Yup.object().shape({
          label: Yup.string().required('Stage Category field is required!'),
          value: Yup.string().required('Stage Category field is required!')
        })
      : Yup.string().required('Stage Category field is required!')
  )
})

const initialState = {
  name: '',
  label: '',
  category: '',
  visibleOnBoardLanes: true,
  color: '',
  orderIndex: 0,
  deals: []
}

const Pipeline = ({ url, texts, admin }) => {
  const [stages, setStages] = useState([])
  const [stageCategories, setStageCategories] = useState([])
  const [formIsVisible, setFormIsVisible] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [pipelineDetails, setPipelineDetails] = useImmer(initialState)
  const [error, setError] = useState('')
  const [loading, isLoading] = useState(false)
  const [isDealsModalVisible, setIsDealsModalVisible] = useState(false)
  const [showPipelineGuideModal, setShowPipelineGuideModal] = useState(false)

  const reduxDispatch = useDispatch()

  useEffect(() => {
    ;(async () => {
      const response = await makeGetRequest(url)
      setStages(toPipelineStages(response))
    })()
  }, [url])

  useEffect(() => {
    ;(async () => {
      const response = await makeGetRequest(`${url}/categories`)
      setStageCategories(response)
    })()
  }, [url])

  const validatePipeline = () => {
    if (stages.some(s => s.name === pipelineDetails.name)) {
      setError('Stage with same name already exists')
      return false
    } else if (stages.some(s => s.label === pipelineDetails.label)) {
      setError('Stage with same label already exists')
      return false
    }
    setError('')
    return true
  }

  const addPipeline = pipeline => {
    editValidationSchema
      .validate(pipeline)
      .then(async function () {
        if (!validatePipeline()) {
          return
        }
        isLoading(true)
        const lastStageIndex = stages.length - 1
        const response = await makePostRequest(
          url,
          fromPipelineDetails(pipeline, stageCategories, stages[lastStageIndex].orderIndex)
        )
        setFormIsVisible(false)
        setError('')
        const newStages = cloneDeep(stages)
        newStages.push(toPipelineStage(response))
        setStages(toPipelineStages(newStages, false))
        setPipelineDetails(() => {
          return initialState
        })
        isLoading(false)
        setShowPipelineGuideModal(true)
      })
      .catch(function (err) {
        setError(err.errors?.length ? err.errors[0] : err.message)
        isLoading(false)
      })
  }

  const updatePipeline = pipeline => {
    editValidationSchema
      .validate(pipeline)
      .then(async function () {
        isLoading(true)
        const response = await makePutRequest(
          `${url}/${pipeline.id}`,
          fromPipelineDetails(pipeline, stageCategories)
        )
        resetForm()
        const index = stages.findIndex(s => s.id === response.id)

        const newState = update(stages, {
          [index]: {
            $set: {
              ...toPipelineStage(response)
            }
          }
        })
        setStages(newState)
        setPipelineDetails(() => {
          return initialState
        })
        isLoading(false)
      })
      .catch(function (err) {
        setError(err.errors?.length ? err.errors[0] : err.message)
        isLoading(false)
      })
  }

  const resetForm = () => {
    setIsEdit(false)
    setFormIsVisible(false)
    setError('')
    setPipelineDetails(() => {
      return initialState
    })
  }

  const openEditStage = stage => {
    setFormIsVisible(true)
    setIsEdit(true)
    setError('')
    setPipelineDetails(state => {
      state.id = stage.id
      state.name = stage.name
      state.label = stage.label
      state.category = {
        value: stage.category.id,
        label: stage.category.name
      }
      state.color = stage.color || stage.category.color
      state.orderIndex = stage.orderIndex
      state.isSystem = stage.system
      state.visibleOnBoardLanes = stage.visibleOnBoardLanes
    })
  }

  const saveStage = async stage => {
    try {
      await makePutRequest(`${url}/${stage.id}`, fromPipelineDetails(stage, stageCategories))
    } catch (e) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'An error occurred. Please contact support',
          level: 'error'
        }
      })
    }
  }

  const moveStage = (stage, direction = 'up') => {
    const stageIndex = stages.findIndex(s => s.id === stage.id)
    const swappedStageIndex = direction === 'up' ? stageIndex - 1 : stageIndex + 1
    const swappedStage = stages[swappedStageIndex]
    const cloneSwappedStage = cloneDeep(swappedStage)

    swappedStage.orderIndex = stage.orderIndex
    stage.orderIndex = cloneSwappedStage.orderIndex

    swappedStage.isFirst = stage.isFirst
    swappedStage.isLast = stage.isLast
    stage.isFirst = cloneSwappedStage.isFirst
    stage.isLast = cloneSwappedStage.isLast

    saveStage(stage)
    saveStage(swappedStage)
    const newState = update(stages, {
      [stageIndex]: {
        $set: { ...swappedStage }
      },
      [swappedStageIndex]: {
        $set: { ...stage }
      }
    })
    setStages(newState)
  }

  const moveStageUp = stage => moveStage(stage, 'up')

  const moveStageDown = stage => moveStage(stage, 'down')

  const changeVisibility = (visibleOnBoardLanes, stage) => {
    stage.visibleOnBoardLanes = visibleOnBoardLanes
    saveStage(stage)

    const stageIndex = stages.findIndex(s => s.id === stage.id)
    const newState = update(stages, {
      [stageIndex]: {
        visibleOnBoardLanes: {
          $set: visibleOnBoardLanes
        }
      }
    })
    setStages(newState)
  }

  const removeStage = async stage => {
    const response = await makeGetRequest(`/v1/deals-board/bydealstatus/${stage.id}`)
    const deals = fromDealsData(response.content)

    if (response.content.length) {
      setPipelineDetails(state => {
        state.id = stage.id
        state.code = stage.code
        state.deals = deals
      })
    } else {
      setPipelineDetails(state => {
        state.id = stage.id
        state.code = stage.code
        state.deals = []
      })
    }

    setIsDealsModalVisible(true)
  }

  const deleteStage = async () => {
    try {
      await makeDeleteRequest(`${url}/${pipelineDetails.id}`)
      const stageIndex = stages.findIndex(s => s.id === pipelineDetails.id)
      if (~stageIndex) {
        const newStages = cloneDeep(stages)
        newStages.splice(stageIndex, 1)
        setStages(toPipelineStages(newStages, false))
        reduxDispatch({
          type: PUSH_NOTIFICATION,
          payload: {
            title: 'Pipeline Stage deleted successfuly',
            level: 'success'
          }
        })
        setIsDealsModalVisible(false)
      }
    } catch (e) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem deleting stage',
          level: 'error'
        }
      })
    }
  }

  const changeDealStatus = async (value, deal) => {
    try {
      const dealsIndex = pipelineDetails.deals.findIndex(d => d.id === deal.id)
      const newState = update(pipelineDetails, {
        deals: {
          [dealsIndex]: {
            $set: { ...deal, status: value }
          }
        }
      })
      setPipelineDetails(() => {
        return newState
      })
      await makePutRequest(`/v1/deals/${deal.id}`, { status: value })
    } catch (e) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem updating deal status',
          level: 'error'
        }
      })
    }
  }

  return (
    <Container>
      {admin && (
        <header className={s.header}>
          <AddButton
            onClick={() => setFormIsVisible(true)}
            disabled={formIsVisible}
            className={s.greenBtn}
            cy="add_pipeline_btn"
          >
            {texts.addButtonText}
          </AddButton>
        </header>
      )}
      {formIsVisible && (
        <AddPipeline
          stageCategories={stageCategories}
          pipelineDetails={pipelineDetails}
          error={error}
          loading={loading}
          isEdit={isEdit}
          resetForm={resetForm}
          updatePipeline={updatePipeline}
          addPipeline={addPipeline}
        />
      )}
      {stages.length > 0 && (
        <List
          stages={stages}
          openEditStage={openEditStage}
          removeStage={removeStage}
          moveStageUp={moveStageUp}
          moveStageDown={moveStageDown}
          changeVisibility={changeVisibility}
          title={texts.title}
          admin={admin}
        />
      )}
      {isDealsModalVisible && (
        <DealsModal
          setIsDealsModalVisible={setIsDealsModalVisible}
          isDealsModalVisible={isDealsModalVisible}
          stages={stages}
          changeDealStatus={changeDealStatus}
          deleteStage={deleteStage}
          stage={pipelineDetails}
        />
      )}
      {showPipelineGuideModal && (
        <PipelineGuideModal
          setShowPipelineGuideModal={setShowPipelineGuideModal}
          showPipelineGuideModal={showPipelineGuideModal}
        />
      )}
    </Container>
  )
}

export default Pipeline
